import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import selectorUser from '@redux/user/selectors';

import {
  getCertificatesByProductIds,
  productLinkCertificate,
  qaSpecLinkCertificate,
  unlinkMultipleDocumentsOrCertificates,
  updateMultipleDocumentsOrCertificates,
} from 'services/product';
import {
  EVENT,
  MEMBER_TYPE_MANUFACTURER,
  MEMBER_TYPE_SHARED_ONLY,
} from 'static/Constants';
import { checkIsSuperAdmin } from 'utils';
import { dialogFunction } from 'common/components';
import * as globalActions from '@redux/global/actions';

import { useCheckPermissions } from 'hooks';
import { PERMISSION_EDIT_QA_SPEC } from 'pages/qa-spec/hooks';

import { qaToggleReloadDocumentCertificateTab } from 'pages/qa-spec/controllers/actions';
import { List, notification, Typography } from 'antd';
import _ from 'lodash';

export const useUpdateCertificate = () => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ['qa-spec', 'qa-spec-data-header'],
    });

    const reloadDocGrid = new CustomEvent(
      EVENT.RELOAD_DOC_GRID_WHEN_ACCEPT_WORKFLOW
    );
    document.dispatchEvent(reloadDocGrid);
    dispatch(globalActions.updateReloadPage(true));
    dispatch(qaToggleReloadDocumentCertificateTab());
  };

  const addMultipleCertificate = useMutation({
    mutationFn: productLinkCertificate,
    onSuccess,
  });

  const addSingleCertificate = useMutation({
    mutationFn: qaSpecLinkCertificate,
    onSuccess,
  });

  const updateMultipleCertificates = useMutation({
    mutationFn: updateMultipleDocumentsOrCertificates,
    onSuccess,
  });

  const unlinkMultipleCertificates = useMutation({
    mutationFn: unlinkMultipleDocumentsOrCertificates,
    onSuccess,
  });

  const getCertificatesForProducts = useMutation({
    mutationFn: getCertificatesByProductIds,
  });

  return {
    addSingleCertificate,
    addMultipleCertificate,
    getCertificatesForProducts,
    updateMultipleCertificates,
    unlinkMultipleCertificates,
  };
};

export const getKeyCertificate = (params) => {
  return ['overview/multiple-panels/get-facts-panel', params];
};

export const useGetCertificatesByProductIds = ({
  params,
  onSuccess,
  ...rest
}) => {
  const { data, ...restQuery } = useQuery({
    queryKey: getKeyCertificate(params),
    queryFn: async () => {
      const { data } = await getCertificatesByProductIds(params);

      onSuccess(data?.productCertificates);

      return data?.productCertificates || [];
    },

    ...rest,
  });

  return {
    listCertificate: data,
    ...restQuery,
  };
};

export const useCheckCertificatePermission = () => {
  const userInfo = useSelector(selectorUser.makeSelectUserInfo());
  const { roles } = userInfo;

  const isSuperAdmin = checkIsSuperAdmin(roles);

  const hasEditQaSpecPermission = useCheckPermissions(PERMISSION_EDIT_QA_SPEC);

  const memberType = userInfo?.member?.memberType;

  const isSupplier =
    MEMBER_TYPE_SHARED_ONLY === memberType ||
    MEMBER_TYPE_MANUFACTURER === memberType;

  const checkCertificatesPermission = () => {
    return isSuperAdmin || (isSupplier && hasEditQaSpecPermission);
  };

  return {
    checkCertificatesPermission,
  };
};

export const useValidateCertificate = ({ onSubmit, isEdit = false }) => {
  const handleValidate = async ({ productCertificates, certificateType }) => {
    const isCertificateTypeActive =
      productCertificates?.length > 0 &&
      productCertificates?.some(({ certificates }) => {
        const hasCertificatedActive =
          certificates?.length > 0 &&
          certificates?.some((certificate) => {
            return (
              // '==' check null or undefined
              certificate?.certificateType == certificateType &&
              certificate?.status?.toLowerCase() === 'active'
            );
          });

        return hasCertificatedActive;
      });

    const isDuplicateCertificate =
      checkDuplicateCertificateType(productCertificates);

    const showConfirm = isEdit
      ? isDuplicateCertificate
      : isCertificateTypeActive;

    if (showConfirm) {
      dialogFunction({
        type: 'warn',
        content: isEdit
          ? 'Updating the Certificate Type will replace the current active one. Do you want to proceed?'
          : 'Uploading a new certificate will replace the current active one. Do you want to proceed?',
        okText: 'Submit',
        okButtonProps: {
          type: 'primary',
        },
        onOk: async () => await onSubmit(),
      });
    } else {
      onSubmit();
    }
  };

  const checkDuplicateCertificateType = (certificates) => {
    const _groupByProductId = _.groupBy(certificates, 'productId');

    for (const [_, productValue] of Object.entries(_groupByProductId)) {
      const certificateTypes =
        productValue?.map(
          ({ certificate }) => certificate?.certificateType || null
        ) || [];

      const hasDuplicateCertificate =
        new Set(certificateTypes)?.size !== certificateTypes.length;

      if (hasDuplicateCertificate) return hasDuplicateCertificate;
    }

    return false;
  };

  const showLockedProducts = (lockedProducts) => {
    const FieldList = (
      <List
        itemLayout='horizontal'
        dataSource={lockedProducts}
        renderItem={(product) => (
          <List.Item style={{ borderBottom: 'none', padding: 0 }}>
            <Typography.Link
              href={`/product/${product?.productId}`}
              target='_blank'
              style={{ fontSize: 14, padding: '2px 16px' }}
            >
              {product?.gtin && `${product?.gtin} - `}
              {product?.productName}
            </Typography.Link>
          </List.Item>
        )}
      />
    );

    notification.error({
      message: (
        <Typography>
          The following QA specifications are locked and cannot be linked to
          certificates:
        </Typography>
      ),
      description: FieldList,
      duration: 5,
    });
  };

  return { handleValidate, showLockedProducts };
};
